<template>
  <v-card flat class="floor-plan-item-list" :data-testid="`${type}-list`">
    <v-alert
      v-if="hasUnplacedItems"
      dense
      type="info"
      text
      class="mx-3 mb-0 text-caption"
    >
      {{ $mpt($t(`${type}.drag_hint`)) }}
    </v-alert>

    <v-card-text class="floor-plan-item-list__content pa-0">
      <v-list dense :key="listKey">
        <v-tooltip
          v-for="item in placeableItems"
          :key="item.id"
          :disabled="!item.isPlacedOnOtherFloorPlan"
          bottom
        >
          <template #activator="{ on, attrs }">
            <v-list-item
              class="floor-plan-item-list__item"
              :class="getItemClass(item)"
              :draggable="!item.isPlacedOnOtherFloorPlan"
              :data-testid="`${type}-item-${item.id}`"
              v-bind="attrs"
              v-on="on"
              @dragstart="handleDragStart($event, item)"
            >
              <v-list-item-icon>
                <v-icon
                  small
                  :color="getIconColor(item)"
                  :aria-label="getStatusLabel(item)"
                >
                  {{ getIcon(item) }}
                </v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <div
                  v-if="typeLabel(item)"
                  class="floor-plan-item-list__type"
                  data-testid="item-type-label"
                >{{ typeLabel(item) }}</div>
                <v-list-item-title
                  class="floor-plan-item-list__name"
                  :title="item.label"
                >
                  {{ item.label }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t(`${type}.count`, { count: item.count }) }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action v-if="item.status" class="floor-plan-item-list__status">
                <status-chip :status="item.status" />
              </v-list-item-action>

              <v-list-item-action v-if="item.isPlaced" class="floor-plan-item-list__remove">
                <v-btn
                  icon
                  x-small
                  :aria-label="$mpt($t(`${type}.remove`, { label: item.label }))"
                  @click="$emit('remove', item.id)"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </template>
          <span>{{ $t('placed_on_other', { name: item.placedFloorPlanName }) }}</span>
        </v-tooltip>

        <template v-if="otherFloorPlanItems.length > 0">
          <list-section-divider
            :label="$t('other_floor_plan_section')"
            testid="other-floor-plan-section-header"
          />
          <v-tooltip
            v-for="item in otherFloorPlanItems"
            :key="item.id"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-list-item
                class="floor-plan-item-list__item"
                :class="getItemClass(item)"
                :draggable="false"
                data-testid="other-floor-plan-item"
                v-bind="attrs"
                v-on="on"
                @dragstart="handleDragStart($event, item)"
              >
                <v-list-item-icon>
                  <v-icon
                    small
                    :color="getIconColor(item)"
                    :aria-label="getStatusLabel(item)"
                  >
                    {{ getIcon(item) }}
                  </v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <div
                    v-if="typeLabel(item)"
                    class="floor-plan-item-list__type"
                    data-testid="item-type-label"
                  >{{ typeLabel(item) }}</div>
                  <v-list-item-title
                    class="floor-plan-item-list__name"
                    :title="item.label"
                  >
                    {{ item.label }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $t(`${type}.count`, { count: item.count }) }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action v-if="item.status" class="floor-plan-item-list__status">
                  <status-chip :status="item.status" />
                </v-list-item-action>
              </v-list-item>
            </template>
            <span>{{ $t('placed_on_other', { name: item.placedFloorPlanName }) }}</span>
          </v-tooltip>
        </template>
      </v-list>
    </v-card-text>
  </v-card>
</template>

<i18n lang="yaml">
ja:
  sensor:
    count: "検出数: {count}"
    drag_hint: センサーを図面にドラッグして配置できます
    remove: "{label}の配置を解除"
  trap:
    count: "捕獲数: {count}"
    drag_hint: 未配置のトラップを図面にドラッグして配置できます
    remove: "{label}の配置を解除"
  placed_on_other: "[{name}] に配置済み"
  other_floor_plan_section: 他の図面に配置済み
  placed: 配置済み
  unplaced: 未配置
en:
  sensor:
    count: "Detections: {count}"
    drag_hint: Drag sensors onto the floor plan to place them
    remove: "Remove {label}"
  trap:
    count: "Capture Count: {count}"
    drag_hint: Drag unplaced traps to the floor plan to position them
    remove: "Remove {label}"
  placed_on_other: "Placed on [{name}]"
  other_floor_plan_section: Placed on another floor plan
  placed: Placed
  unplaced: Unplaced
</i18n>

<script>
import ListSectionDivider from '@/components/atoms/ListSectionDivider';
import StatusChip from '@/components/atoms/StatusChip';
import SensorTypes from '@/mixins/sensorTypes';

export default {
  name: 'FloorPlanItemList',
  components: {
    ListSectionDivider,
    StatusChip,
  },
  mixins: [SensorTypes],
  props: {
    items: {
      type: Array,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: v => ['sensor', 'trap'].includes(v),
    },
    dragDataKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      dragImage: null,
    };
  },
  created() {
    this.initializeDragImage();
  },
  computed: {
    listKey() {
      return this.items.map(
        i => `${i.id}-${i.isPlaced}-${i.isPlacedOnOtherFloorPlan}-${i.count}`,
      ).join(',');
    },
    hasUnplacedItems() {
      return this.items.some(item => !item.isPlaced && !item.isPlacedOnOtherFloorPlan);
    },
    placeableItems() {
      return this.sortWithinSection(
        this.items.filter(item => !item.isPlacedOnOtherFloorPlan),
      );
    },
    otherFloorPlanItems() {
      return this.sortWithinSection(
        this.items.filter(item => item.isPlacedOnOtherFloorPlan),
      );
    },
  },
  methods: {
    sortWithinSection(items) {
      return [...items].sort((a, b) => {
        const groupA = this.sortGroup(a);
        const groupB = this.sortGroup(b);
        if (groupA !== groupB) return groupA - groupB;

        return b.count - a.count;
      });
    },
    // 配置済み → 未配置 → 休止中 の順に並べる。
    // 休止中は配置の有無によらず最後にまとめる（操作対象から外れているため）。
    sortGroup(item) {
      if (this.isInactive(item)) return 2;
      return item.isPlaced ? 0 : 1;
    },
    isInactive(item) {
      return item.status === 'inactive';
    },
    typeLabel(item) {
      return item.type ? this.SENSOR_TYPE_NAMES[item.type] : null;
    },
    getItemClass(item) {
      return {
        'floor-plan-item-list__item--unplaced': !item.isPlaced && !item.isPlacedOnOtherFloorPlan,
        'floor-plan-item-list__item--on-other': item.isPlacedOnOtherFloorPlan,
        'floor-plan-item-list__item--inactive': this.isInactive(item),
      };
    },
    getIconColor(item) {
      if (item.isPlaced) return 'success';
      if (item.isPlacedOnOtherFloorPlan) return 'warning';
      return 'grey';
    },
    getIcon(item) {
      if (item.isPlaced) return 'mdi-check-circle';
      if (item.isPlacedOnOtherFloorPlan) return 'mdi-check-circle';
      return 'mdi-checkbox-blank-circle-outline';
    },
    getStatusLabel(item) {
      if (item.isPlaced) return this.$t('placed');
      if (item.isPlacedOnOtherFloorPlan) {
        return this.$t('placed_on_other', { name: item.placedFloorPlanName });
      }
      return this.$t('unplaced');
    },
    initializeDragImage() {
      const svgData = this.createDragImageSvg();
      this.dragImage = this.createImageFromSvg(svgData);
    },
    createDragImageSvg() {
      const SIZE = 100;
      const MARKER_SIZE = 16;
      const uniqueId = `${this.type}-${Math.random().toString(36).substring(2, 11)}`;
      return [
        `<svg width="${SIZE}" height="${SIZE}" xmlns="http://www.w3.org/2000/svg">`,
        '<defs>',
        `<radialGradient id="${uniqueId}">`,
        '<stop offset="0%" stop-color="rgba(128, 128, 128, 0.7)"/>',
        '<stop offset="100%" stop-color="rgba(128, 128, 128, 0.3)"/>',
        '</radialGradient>',
        '</defs>',
        `<circle cx="50" cy="50" r="50" fill="url(#${uniqueId})"/>`,
        `<rect x="42" y="42" width="${MARKER_SIZE}" height="${MARKER_SIZE}"`,
        ' fill="#FFD700" stroke="black" stroke-width="2"/>',
        '</svg>',
      ].join('');
    },
    createImageFromSvg(svgData) {
      if (typeof URL === 'undefined' || !URL.createObjectURL) {
        return null;
      }
      const blob = new Blob([svgData], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => URL.revokeObjectURL(url);
      img.src = url;
      return img;
    },
    handleDragStart(event, item) {
      if (item.isPlacedOnOtherFloorPlan) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.setData(this.dragDataKey, item.id);
      event.dataTransfer.effectAllowed = 'move';
      if (this.dragImage) {
        event.dataTransfer.setDragImage(this.dragImage, 50, 50);
      }
    },
  },
};
</script>

<style scoped>
.floor-plan-item-list {
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.floor-plan-item-list__content {
  overflow-y: auto;
  flex: 1;
}

.floor-plan-item-list__item {
  border-bottom: 1px solid #e0e0e0;
  cursor: grab;
  /* 右側に配置解除ボタン分の幅を常に確保し、ボタンの有無で
     監視状況チップの位置がずれないようにする
     （デザイン正本 mockup-図面.html の .sensor-item と同じ考え方） */
  position: relative;
  padding-right: 42px;
}

/* 配置解除ボタンは行の右端に絶対配置する。
   通常フローに置くとボタンを持つ行だけチップが左へ押される */
.floor-plan-item-list__remove {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  min-width: 0;
}

.floor-plan-item-list__item:hover {
  background-color: #fafafa;
}

.floor-plan-item-list__item:active {
  cursor: grabbing;
}

.floor-plan-item-list__item:last-child {
  border-bottom: none;
}

/* 未配置: 透過は使わず名前の文字色を落とす。
   透過は休止中の表現に一本化する */
.floor-plan-item-list__item--unplaced .floor-plan-item-list__name {
  color: #9e9e9e;
}

/* 他の図面に配置済み: 区切りセクションで状態が読めるため透過は使わない */
.floor-plan-item-list__item--on-other {
  cursor: not-allowed;
}

/* 休止中: 図面上のマーカーと同じ表現。ホバーでも解除しない。
   数値は floorPlanItemDisplay.js の INACTIVE_OPACITY と対で管理する
   （CSS から JS 定数を参照できないため、両方を変更する必要がある） */
.floor-plan-item-list__item--inactive {
  opacity: 0.45;
}

/* 状態アイコンは Vuetify 既定で上寄せになるが、
   種別ラベルを足して行が 3 段になったため中央に揃える */
.floor-plan-item-list__item .v-list-item__icon {
  align-self: center;
  margin-top: 0;
  margin-bottom: 0;
}

/* 監視状況のチップ。v-list-item-action の既定の左右マージンだと
   検出数の行と離れすぎるため詰める */
.floor-plan-item-list__status {
  margin: 0;
  align-self: center;
}

.floor-plan-item-list__type {
  font-size: 11px;
  color: #9e9e9e;
  line-height: 1.3;
  letter-spacing: 0.03em;
}
</style>
